"use client";
import { useTranslations } from "@zapier/i18n";

import { useCallback, useMemo, useState } from "react";
import {
  Text,
  ToggleSwitch,
  Tooltip,
  TooltipFocusableTrigger,
  usePress,
} from "@zapier/design-system";
import {
  getMessageFromNetworkError,
  useDisableZap,
  useEnableZap,
  useIsApprovalRequiredForZap,
  useIsValidZdl,
  useUserAccountRole,
  useZapApprovals,
  Zap,
} from "@zapier/client";
import {
  useNotification,
  ApprovalType,
  ApprovalRequiredOperation,
} from "@zapier/utils";
import styles from "./ZapPauseToggle.module.css";
import { ZapModal } from "../types";
import {
  useEmitZapRowToggleZapEvent,
  ZapRowToggleZapInteractionOption,
} from "../../../events";

interface Props {
  isSharedFolder?: boolean;
  setApprovalType?: (approvalType: ApprovalType) => void;
  setCurrentModal?: (modal?: ZapModal | undefined) => void;
  setCurrentZap?: (zap?: Zap | undefined) => void;
  zapCurrentVersionId: string | undefined;
  zapDeletedAt: string | null | undefined;
  zapId: string;
  zapIsEnabled: boolean;
  zapIsPaused: boolean;
  zapTitle: string;
  zapZdl: any | undefined;
  canEnableDisable?: boolean;
  folderId?: string; // used for cache invalidation
}

export const ZapPauseToggle = ({
  setApprovalType,
  setCurrentModal,
  setCurrentZap,
  zapCurrentVersionId,
  zapDeletedAt,
  zapId,
  zapIsEnabled,
  zapIsPaused,
  zapTitle,
  zapZdl,
  canEnableDisable = true,
  folderId,
}: Props) => {
  const t = useTranslations("zap-pause-toggle");

  const { createNotification } = useNotification();
  const { isValidZdl } = useIsValidZdl();
  const { enableZap, isLoading: isEnableZapLoading } = useEnableZap({
    folderId,
  });
  const { disableZap, isLoading: isDisableZapLoading } = useDisableZap({
    folderId,
  });
  const { emit } = useEmitZapRowToggleZapEvent();
  const [isToggling, setIsToggling] = useState(false);
  const {
    isApprovalLoading,
    pendingEnableOrDisableApproval,
    userPendingApproval,
  } = useZapApprovals({
    zapId,
  });
  const isApprovalRequired = useIsApprovalRequiredForZap({
    zapId,
    isZapPublished: !!zapCurrentVersionId,
    operation: zapIsEnabled
      ? ApprovalRequiredOperation.TurnOff
      : ApprovalRequiredOperation.Toggle,
  });
  const { customuserId: userId } = useUserAccountRole();

  const isValid = useMemo(() => {
    const v = !!isValidZdl(zapZdl);
    return v;
  }, [zapZdl, zapIsEnabled]);

  const disabled = useMemo(() => {
    if (!canEnableDisable) {
      return true;
    }
    return !!(
      zapDeletedAt ||
      zapIsPaused ||
      // Zaps that are enabled and invalid shouldn't show as disabled
      (!isValid && !zapIsEnabled && !!zapId) ||
      isEnableZapLoading ||
      isDisableZapLoading ||
      isApprovalLoading ||
      (isApprovalRequired &&
        pendingEnableOrDisableApproval &&
        pendingEnableOrDisableApproval.requestedByUserId !== userId) ||
      (isApprovalRequired &&
        userPendingApproval &&
        !pendingEnableOrDisableApproval) ||
      // require that a published version is present to enable the zap
      !zapCurrentVersionId
    );
  }, [
    isApprovalLoading,
    isApprovalRequired,
    isDisableZapLoading,
    isEnableZapLoading,
    isValid,
    zapDeletedAt,
    zapId,
    zapIsEnabled,
    zapIsPaused,
    canEnableDisable,
    zapCurrentVersionId,
  ]);

  const tooltipText = useMemo(() => {
    if (!canEnableDisable) {
      return zapIsEnabled
        ? t("tooltips.no-permission-disable")
        : t("tooltips.no-permission-enable");
    }
    if (
      isApprovalRequired &&
      pendingEnableOrDisableApproval &&
      pendingEnableOrDisableApproval.requestedByUserId !== userId
    ) {
      return zapIsEnabled
        ? t("tooltips.request-pending-turn-off")
        : t("tooltips.request-pending-turn-on");
    }
    if (
      isApprovalRequired &&
      userPendingApproval &&
      !pendingEnableOrDisableApproval
    ) {
      return t("tooltips.user-has-pending-request");
    }

    if (zapIsPaused) {
      return t("tooltips.zap-has-held-tasks");
    }
    if (zapIsEnabled) {
      return t("tooltips.zap-is-on");
    }
    if (zapDeletedAt) {
      return t("tooltips.zap-in-trash");
    }
    if (!isValid || !zapCurrentVersionId) {
      return t("tooltips.zap-incomplete");
    }
    return t("tooltips.zap-is-off");
  }, [
    t,
    isApprovalRequired,
    isValid,
    pendingEnableOrDisableApproval,
    userId,
    userPendingApproval,
    zapDeletedAt,
    zapIsEnabled,
    zapIsPaused,
    canEnableDisable,
    zapCurrentVersionId,
  ]);

  const handleToggleZap = useCallback(() => {
    // If toggle is disabled, show error notification
    if (disabled) {
      createNotification({
        title: t("errors.unable-to-turn-on", { zapTitle }),
        message: t("errors.zap-is-disabled"),
        type: "error",
      });
      return;
    }

    if (
      isApprovalRequired &&
      setApprovalType &&
      setCurrentZap &&
      setCurrentModal
    ) {
      setApprovalType(
        zapIsEnabled ? ApprovalType.TurnOff : ApprovalType.TurnOn,
      );
      setCurrentZap();
      setCurrentModal(ZapModal.Approval);
      return;
    }

    setIsToggling(true);

    // If zap is enabled/on, call disable/turn off zap
    if (zapIsEnabled) {
      emit(ZapRowToggleZapInteractionOption.Disable);
      return disableZap(
        {
          zapId,
        },
        {
          onSuccess: () => {
            createNotification({
              message: (
                <Text>{t("messages.success-turned-off", { zapTitle })}</Text>
              ),
              type: "success",
            });
          },
          onError: (error: any) => {
            createNotification({
              title: t("errors.unable-to-turn-off", { zapTitle }),
              message: getMessageFromNetworkError(error),
              type: "error",
            });
          },
          onSettled: () => {
            setIsToggling(false);
          },
        },
      );
    }

    emit(ZapRowToggleZapInteractionOption.Enable);
    return enableZap(
      {
        zapId,
        currentVersionId: zapCurrentVersionId,
      },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>{t("messages.success-turned-on", { zapTitle })}</Text>
            ),
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: t("errors.unable-to-turn-on", { zapTitle }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
        onSettled: () => {
          setIsToggling(false);
        },
      },
    );
  }, [
    disabled,
    isApprovalRequired,
    setApprovalType,
    setIsToggling,
    zapCurrentVersionId,
    zapId,
    zapIsEnabled,
    zapTitle,
  ]);

  const { pressProps } = usePress({
    onPress: (event) => {
      event.continuePropagation();
      handleToggleZap();
    },
  });

  return (
    <div
      data-checked={!!zapIsEnabled}
      data-disabled={disabled}
      data-testid="ZapPauseToggle"
      id={zapId}
      className={styles.zapPauseToggle}
      {...pressProps}
    >
      <Tooltip allowMultiline content={tooltipText}>
        <TooltipFocusableTrigger>
          <ToggleSwitch
            {...pressProps}
            aria-label={t("aria.toggle-zap", { zapTitle })}
            checked={!!zapIsEnabled}
            disabled={disabled}
            id={`ZapPauseToggle-${zapId}`}
            isLoading={isToggling}
            showDisabledTooltip={false}
            onChange={handleToggleZap}
          />
        </TooltipFocusableTrigger>
      </Tooltip>
    </div>
  );
};
